import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, Paperclip } from "lucide-react";
import { UserAvatar, VerifiedTick } from "@/components/UserAvatar";
import { Input } from "@/components/ui/input";
import { api, ago, mediaUrl } from "@/lib/api";
import { professionOf } from "@/lib/constants";

function usePoll(fn, ms, deps) {
  useEffect(() => {
    fn();
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function ConversationList({ activeId, convs }) {
  return (
    <div className={`tm-card flex-col overflow-hidden ${activeId ? "hidden md:flex" : "flex"}`} data-testid="conversation-list">
      <div className="border-b p-5"><h2 className="font-display text-lg font-bold text-tm-green">Messages</h2></div>
      <div className="flex-1 overflow-y-auto">
        {convs?.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="conversations-empty">No conversations yet. Message a professional from Discover.</p>}
        {(convs || []).map((c) => (
          <Link key={c.id} to={`/messages/${c.other?.id}`} data-testid={`conversation-${c.other?.id}`}
            className={`flex items-center gap-3 border-b px-5 py-4 transition-colors duration-200 ${activeId === c.other?.id ? "bg-tm-muted" : "hover:bg-tm-sand"}`}>
            <UserAvatar user={c.other} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-semibold">{c.other?.full_name}</p><span className="shrink-0 text-[11px] text-slate-400">{ago(c.updated_at)}</span></div>
              <p className="truncate text-xs text-slate-500">{c.last_message || "Start the conversation"}</p>
            </div>
            {c.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-tm-saffron px-1.5 text-[11px] font-bold text-tm-dark">{c.unread}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

function Bubble({ m }) {
  return (
    <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.mine ? "rounded-br-sm bg-tm-green text-white" : "rounded-bl-sm border bg-white text-slate-800"}`}>
        {m.media && <img src={mediaUrl(m.media)} alt="" className="mb-2 max-h-64 rounded-lg" />}
        {m.file_url && <a href={mediaUrl(m.file_url)} target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-1 underline"><Paperclip className="h-3.5 w-3.5" />{m.file_name || "Attachment"}</a>}
        {m.text && <p className="whitespace-pre-line">{m.text}</p>}
        <p className={`mt-1 text-[10px] ${m.mine ? "text-white/60" : "text-slate-400"}`}>{ago(m.created_at)}</p>
      </div>
    </div>
  );
}

function Thread({ otherId, other }) {
  const [msgs, setMsgs] = useState([]);
  const [person, setPerson] = useState(other);
  const [text, setText] = useState("");
  const endRef = useRef();

  useEffect(() => {
    if (other) setPerson(other);
    else api.get(`/users/${otherId}`).then(({ data }) => setPerson(data)).catch(() => {});
  }, [otherId, other]);
  usePoll(() => api.get(`/conversations/${otherId}/messages`).then(({ data }) => setMsgs(data)).catch(() => {}), 4000, [otherId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text;
    setText("");
    const { data } = await api.post("/messages", { to: otherId, text: body });
    setMsgs((ms) => [...ms, { id: Date.now(), text: body, created_at: new Date().toISOString(), ...(data?.id ? data : {}), mine: true }]);
  };

  return (
    <div className="tm-card flex flex-col overflow-hidden" data-testid="message-thread">
      <div className="flex items-center gap-3 border-b p-4">
        <Link to="/messages" className="md:hidden" data-testid="thread-back-button"><ArrowLeft className="h-5 w-5" /></Link>
        {person && <Link to={`/profile/${otherId}`} className="flex items-center gap-3"><UserAvatar user={person} size="sm" />
          <div><p className="flex items-center gap-1 text-sm font-semibold">{person.full_name}<VerifiedTick user={person} /></p><p className="text-xs text-slate-500">{professionOf(person)}</p></div></Link>}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto bg-tm-sand/60 p-5">
        {msgs.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t p-3">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message" className="rounded-full" data-testid="message-input" />
        <button type="submit" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-tm-green text-white" data-testid="message-send-button"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { otherId } = useParams();
  const [convs, setConvs] = useState(null);
  usePoll(() => api.get("/conversations").then(({ data }) => setConvs(data)).catch(() => setConvs([])), 6000, [otherId]);
  const other = convs?.find((c) => c.other?.id === otherId)?.other;
  return (
    <div className="mx-auto grid h-[calc(100vh-10rem)] max-w-6xl gap-4 md:grid-cols-[340px_1fr] lg:h-[calc(100vh-7rem)]">
      <ConversationList activeId={otherId} convs={convs} />
      {otherId ? <Thread otherId={otherId} other={other} /> : (
        <div className="tm-card hidden place-items-center md:grid" data-testid="messages-placeholder">
          <div className="text-center"><MessageSquare className="mx-auto h-10 w-10 text-tm-gold" /><p className="mt-3 font-display font-bold text-tm-green">Select a conversation</p></div>
        </div>
      )}
    </div>
  );
}
